<template>
    <p v-if="hasEvidence && appearance === 'links'" class="min-w-0 scaffold-details-small text-ink-muted text-pretty">
        <span v-if="showLabel">Based on: </span>
        <template v-for="(group, index) in sortedGroups" :key="group.kind">
            <BasePopover
                align="start"
                :side-offset="6"
                trigger-class="underline decoration-current underline-offset-2 hover:text-ink focus-ring"
                content-class="w-80 max-w-[calc(100vw-2rem)] p-3 shadow-lg"
                @update:open="handlePopoverOpen($event, [group], false)">
                <template #trigger>{{ evidenceLinkText(group) }}</template>
                <template #content>
                    <EvidencePopoverContent :groups="[group]" @evidence-link-clicked="handleEvidenceLinkClick" />
                </template>
            </BasePopover>
            <template v-if="index < sortedGroups.length - 1">{{ index === sortedGroups.length - 2 ? " and " : ", " }}</template>
        </template>
    </p>

    <div v-else-if="hasEvidence" ref="containerRef" class="relative flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-hidden">
        <span v-if="showLabel" ref="labelRef" class="shrink-0 scaffold-details-small text-ink-muted text-pretty">Based on:</span>

        <BasePopover
            v-for="group in visibleGroups"
            :key="group.kind"
            as-child
            align="start"
            :side-offset="6"
            content-class="w-80 max-w-[calc(100vw-2rem)] p-3 shadow-lg"
            @update:open="handlePopoverOpen($event, [group], false)">
            <template #trigger>
                <BaseButton
                    type="button"
                    theme="neutral"
                    size="sm"
                    class="inline-flex shrink-0 items-center gap-2"
                    :aria-label="`View ${evidenceChipLabel(group)}: ${group.count} evidence ${group.count === 1 ? 'item' : 'items'}`">
                    <EvidenceSourceIcon :kind="group.kind" />
                    <span class="text-pretty">{{ evidenceChipLabel(group) }}</span>
                    <span v-if="group.count > 1 && group.kind !== 'user_profile_note'" class="text-ink-muted">{{ group.count }}</span>
                </BaseButton>
            </template>
            <template #content>
                <EvidencePopoverContent :groups="[group]" @evidence-link-clicked="handleEvidenceLinkClick" />
            </template>
        </BasePopover>

        <BasePopover
            v-if="overflowGroups.length"
            as-child
            align="start"
            :side-offset="6"
            content-class="w-80 max-w-[calc(100vw-2rem)] p-3 shadow-lg"
            @update:open="handlePopoverOpen($event, overflowGroups, true)">
            <template #trigger>
                <BaseButton
                    type="button"
                    theme="neutral"
                    size="sm"
                    class="shrink-0"
                    :aria-label="`View ${overflowEvidenceCount} hidden evidence ${overflowEvidenceCount === 1 ? 'item' : 'items'} from ${overflowGroups.length} source ${overflowGroups.length === 1 ? 'type' : 'types'}`">
                    {{ overflowLabel }}
                </BaseButton>
            </template>
            <template #content>
                <EvidencePopoverContent :groups="overflowGroups" @evidence-link-clicked="handleEvidenceLinkClick" />
            </template>
        </BasePopover>

        <div ref="measurementRef" class="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2 whitespace-nowrap" aria-hidden="true" inert>
            <BaseButton
                v-for="group in sortedGroups"
                :key="group.kind"
                theme="neutral"
                size="sm"
                tabindex="-1"
                data-evidence-chip-measure
                class="inline-flex shrink-0 items-center gap-2">
                <EvidenceSourceIcon :kind="group.kind" />
                <span>{{ evidenceChipLabel(group) }}</span>
                <span v-if="group.count > 1 && group.kind !== 'user_profile_note'" class="text-ink-muted">{{ group.count }}</span>
            </BaseButton>
            <BaseButton ref="overflowMeasurementRef" theme="neutral" size="sm" tabindex="-1" class="shrink-0">+0</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import {
    calculateVisibleEvidenceGroupCount,
    evidenceChipLabel,
    evidenceLinkText,
    evidenceSourceCounts,
    type InsightEvidenceAnalyticsContext,
    type InsightEvidenceGroup,
    type InsightEvidenceItem,
    sortEvidenceGroups,
} from "~vue/Labs/evidence";
import EvidenceSourceIcon from "~vue/Labs/EvidenceSourceIcon.vue";
import EvidencePopoverContent from "~vue/Labs/InsightEvidencePopoverContent.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        evidenceGroups?: InsightEvidenceGroup[];
        maxVisible?: number;
        analyticsContext?: InsightEvidenceAnalyticsContext;
        showLabel?: boolean;
        /** "chips" is the control pill-chip row; "links" is the redesigned inline-text treatment. */
        appearance?: "chips" | "links";
    }>(),
    {
        evidenceGroups: () => [],
        showLabel: true,
        appearance: "chips",
    },
);

const containerRef = ref<HTMLElement>();
const labelRef = ref<HTMLElement>();
const measurementRef = ref<HTMLElement>();
const overflowMeasurementRef = ref<InstanceType<typeof BaseButton>>();
const visibleCount = ref(props.maxVisible ?? props.evidenceGroups.length);
let resizeObserver: ResizeObserver | undefined;

const sortedGroups = computed(() => sortEvidenceGroups(props.evidenceGroups));
const visibleGroups = computed(() => sortedGroups.value.slice(0, visibleCount.value));
const overflowGroups = computed(() => sortedGroups.value.slice(visibleCount.value));
const overflowEvidenceCount = computed(() => overflowGroups.value.reduce((total, group) => total + group.count, 0));
const overflowLabel = computed(() => (visibleGroups.value.length ? `+${overflowEvidenceCount.value}` : `${overflowEvidenceCount.value}`));
const hasEvidence = computed(() => sortedGroups.value.length > 0);

function measureVisibleGroups() {
    const groups = sortedGroups.value;
    if (props.maxVisible !== undefined) {
        visibleCount.value = Math.min(props.maxVisible, groups.length);
        return;
    }

    const containerWidth = containerRef.value?.clientWidth ?? 0;
    const labelWidth = labelRef.value?.offsetWidth ?? 0;
    const groupWidths = Array.from(measurementRef.value?.querySelectorAll<HTMLElement>("[data-evidence-chip-measure]") ?? []).map((element) => element.offsetWidth);
    const overflowElement: HTMLElement | undefined = overflowMeasurementRef.value?.$el;
    if (!containerWidth || (props.showLabel && !labelWidth) || groupWidths.length !== groups.length || !overflowElement) {
        visibleCount.value = groups.length;
        return;
    }

    const overflowWidths = groups.map((_, index) => {
        const hiddenGroupCount = index + 1;
        const hiddenEvidenceCount = groups.slice(-hiddenGroupCount).reduce((total, group) => total + group.count, 0);
        overflowElement.textContent = hiddenGroupCount === groups.length ? `${hiddenEvidenceCount}` : `+${hiddenEvidenceCount}`;
        return overflowElement.offsetWidth;
    });
    visibleCount.value = calculateVisibleEvidenceGroupCount(containerWidth, labelWidth, groupWidths, overflowWidths);
}

function scheduleMeasurement() {
    void nextTick(measureVisibleGroups);
}

onMounted(() => {
    scheduleMeasurement();
    if (containerRef.value && "ResizeObserver" in globalThis) {
        resizeObserver = new ResizeObserver(scheduleMeasurement);
        resizeObserver.observe(containerRef.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = undefined;
});

watch(() => props.evidenceGroups, scheduleMeasurement, { deep: true });

function analyticsProperties() {
    if (!props.analyticsContext) {
        return undefined;
    }
    return {
        insight_id: props.analyticsContext.insightId,
        experiment_slug: props.analyticsContext.experimentSlug,
        location: props.analyticsContext.location,
    };
}

function handlePopoverOpen(open: boolean, groups: InsightEvidenceGroup[], isOverflow: boolean) {
    if (!open || !props.analyticsContext) {
        return;
    }

    void logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_EVIDENCE_VIEWED, {
        ...analyticsProperties(),
        evidence_source_counts: evidenceSourceCounts(groups),
        is_overflow: isOverflow,
    });
}

function handleEvidenceLinkClick(group: InsightEvidenceGroup, item: InsightEvidenceItem) {
    if (!props.analyticsContext) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_EVIDENCE_LINK_CLICKED, {
        ...analyticsProperties(),
        evidence_source_counts: { [group.kind]: 1 },
        is_external: Boolean(item.external),
    });
}
</script>
